import {
  getDeleteApiCertificationProgramIdMutationOptions,
  type CertificationProgramListItemDTO,
} from '@smartsash/api-client';
import { usePermission } from '@smartsash/permissions';
import { useEntityDelete } from '@smartsash/server-ops';
import { Badge, Icon, openModal } from '@smartsash/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './Certifications.module.css';

import { ADMIN_LIST_SEARCH_DEFAULTS } from '@/router/listSearch';
import { MobileCardActions, type RowActionEntry } from '@/ui/shared/RowActions';
import { certificationProgramName } from '@/utils/certification/certificationProgramName';

/**
 * The mobile row for the Certifications list (HS-1162).
 *
 * It reads the DTO the list query returns. Nothing here needs a lookup join: `labName` and
 * `institutionName` come down on the row itself, unlike the Groups and Devices cards.
 *
 * The three "view laboratory"/"view institute" entries used to be inert — the mock's menu items
 * closed the menu and did nothing. They navigate now, and delete is real.
 */
function MobileCard({ row }: { row: CertificationProgramListItemDTO }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermission();
  const id = row.id ?? '';
  const canManage = can('certificationProgram.delete');
  const active = row.status === 'active';
  const del = useEntityDelete({
    entity: 'certificationProgram',
    mutationOptions: getDeleteApiCertificationProgramIdMutationOptions,
  });

  const handleDelete = useCallback(async () => {
    const { action } = await openModal({
      title: t('certifications.deleteTitle', {
        name: certificationProgramName(row, row.institutionName ?? ''),
      }),
      description: t('certifications.deleteDescription'),
      variant: 'error',
      icon: <Icon name="trash-01" />,
      actions: [
        { label: t('common.cancel'), value: 'cancel' },
        { label: t('common.delete'), value: 'confirm', destructive: true },
      ],
    }).closed;

    if (action === 'confirm') del.mutate({ id });
  }, [del, id, row, t]);

  const items: RowActionEntry[] = [
    // DV-01: the card title opens the read view, and so does this entry; edit is separate.
    {
      label: t('common.view'),
      icon: 'eye',
      onClick: () => {
        void navigate({ to: '/certifications/$id', params: { id } });
      },
    },
    {
      label: t('common.edit'),
      icon: 'edit-01',
      // HS-1396: mirrors `./RowActions.tsx` — Delete was gated through `canManage`, Edit was not.
      permission: 'certificationProgram.edit',
      onClick: () => {
        void navigate({ to: '/certifications/$id/edit', params: { id } });
      },
    },
    null,
    // Institution-scoped programmes (HS-1366) have no laboratory to open: the list filters by
    // name, so a fallback here would search the laboratories for an institution's name instead.
    ...(row.labName == null || row.labName === ''
      ? []
      : ([
          {
            label: t('certifications.actions.viewLaboratory'),
            icon: 'lab',
            onClick: () => {
              void navigate({
                to: '/laboratories',
                search: { ...ADMIN_LIST_SEARCH_DEFAULTS, q: row.labName ?? '' },
              });
            },
          },
        ] satisfies RowActionEntry[])),
    {
      label: t('certifications.actions.viewInstitute'),
      icon: 'building-07',
      onClick: () => {
        void navigate({
          to: '/institutions',
          search: { ...ADMIN_LIST_SEARCH_DEFAULTS, q: row.institutionName ?? '' },
        });
      },
    },
    ...(canManage
      ? ([
          null,
          {
            label: t('common.delete'),
            icon: 'trash-01',
            onClick: () => {
              void handleDelete();
            },
          },
        ] satisfies RowActionEntry[])
      : []),
  ];

  return (
    <div className={styles['m-card']}>
      <Link to="/certifications/$id" params={{ id }} className={styles['m-card-title']}>
        {certificationProgramName(row, row.institutionName ?? '')}
      </Link>
      <div className={styles['m-card-badges']}>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="building-07" />}>
          {row.institutionName}
        </Badge>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="refresh-cw-05" />}>
          {t('certifications.mobile.daysCount', { count: row.validityDays ?? 0 })}
        </Badge>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="play-square" />}>
          {t('certifications.mobile.videosCount', { count: row.videoCount ?? 0 })}
        </Badge>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="help-circle" />}>
          {t('certifications.mobile.questionsCount', { count: row.questionCount ?? 0 })}
        </Badge>
        <Badge variant="modern" size="sm" dot color={active ? 'success' : 'gray'}>
          {active ? t('certifications.status.active') : t('certifications.status.inactive')}
        </Badge>
      </div>
      <MobileCardActions items={items} />
    </div>
  );
}

export default MobileCard;
